import { useEffect, useState } from 'react';
import { catchError, EMPTY, tap } from 'rxjs';

import useSecuredApiV2 from './use-secured-api-v2';
import { type CustomerWithSitesDto } from '../dto/customer/customer-with-sites-dto';
import { type ResponseDto } from '../dto/ResponseDto';
import { ACTIVE_CUSTOMERS_WITH_SITES_END_POINT_URL } from '../utils/endpoints';
import type ProblemDetailsDto from '../dto/error/problem-details-dto';

export interface ActiveCustomersWithSitesResult {
  data: CustomerWithSitesDto[];
  isLoading: boolean;
  errorMessage?: string;
}

/**
 * Loads the active customers with all of their sites, sorted by customer name, from the customer
 * controller. Only HHC_ADMIN may load them, so nothing is requested while "enabled" is false.
 */
const useActiveCustomersWithSites = (enabled: boolean): ActiveCustomersWithSitesResult => {
  const api = useSecuredApiV2();
  const [state, setState] = useState<ActiveCustomersWithSitesResult>({
    data: [],
    isLoading: enabled,
  });

  useEffect(() => {
    if (!enabled) {
      setState({ data: [], isLoading: false });
      return;
    }
    setState((previous) => ({ ...previous, isLoading: true, errorMessage: undefined }));

    const subscription = api
      .get(ACTIVE_CUSTOMERS_WITH_SITES_END_POINT_URL, {}, {})
      .pipe(
        tap((response: ResponseDto<CustomerWithSitesDto[]>) => {
          setState({ data: response.data, isLoading: false });
        }),
        catchError((error: ProblemDetailsDto) => {
          setState({
            data: [],
            isLoading: false,
            errorMessage: error.detail ?? 'Failed to load customers',
          });
          return EMPTY;
        })
      )
      .subscribe();

    // Ignore the response if the component unmounts before it arrives
    return () => {
      subscription.unsubscribe();
    };
  }, [enabled]);

  return state;
};

export default useActiveCustomersWithSites;
